import { useStores } from '@littlebird/sdk';
import { useNavigate, useParams } from '@tanstack/react-router';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { Button, Header, IconButton } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { EditableTitle } from '@/components/ui/EditableTitle';
import { XStack } from '@/components/ui/layout/layout';
import { useHashRoute } from '@/hooks/useHashRoute';
import { trackEvent } from '@/utils/analytics';
import { formatDateHumanReadable } from '@/utils/date';

import { journalApis } from './journal.apis';
import { journalStore } from './journal.store';

const JournalsHeader = observer(() => {
  const navigate = useNavigate();
  const { journals: journalsStore } = useStores();
  const { journalId } = useParams({ strict: false });
  const [isDeleting, setIsDeleting] = useState(false);
  const restoreJournal = journalApis.useRestoreJournal();
  const updateJournal = journalApis.useUpdateJournal();

  const { setHash } = useHashRoute();

  const journal = journalId
    ? computed(() => journalsStore.data.get(journalId)).get()
    : null;

  const deleteJournal = useCallback(async () => {
    if (!journal || isDeleting) return;

    setIsDeleting(true);
    try {
      await journal.delete();
      navigate({ to: '/journal' });
      trackEvent('JOURNAL_DELETED', {
        journalId: journal.id,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [journal, navigate, isDeleting]);

  return (
    <Header
      left={
        journal?.sections?.title && (
          <EditableTitle
            key={`editable-title-${journal.id}`}
            value={journal.sections.title}
            onEdit={(title) => {
              if (title.trim() === (journal.sections?.title ?? '').trim())
                return;
              updateJournal(journal.id, { title });
            }}
          />
        )
      }
      right={
        <XStack className="gap-0.5 items-center">
          {journal && <DateLabel journalId={journal.id} />}
          {journal && (
            <>
              <CopyButton
                text={journal.text}
                copyText="Copy"
                icon={Icons.copy}
                size="sm"
              />
            </>
          )}
          <ShowHistoryButton />
          {journal && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger variant="ghost">
                <Icons.moreHorizontal className="size-4" />
              </DropdownMenu.Trigger>

              <DropdownMenu.Content
                align="end"
                side="bottom"
                className="max-w-[9rem]"
              >
                <DropdownMenu.Item
                  onSelect={() => {
                    setHash('settings/Journal');
                    trackEvent('USER_ACTION_EVENT', {
                      actionName: 'Journal_Settings_Opened',
                    });
                  }}
                >
                  <span>
                    <Icons.settings className="size-4" />
                  </span>
                  <span className="text-foreground">Configure</span>
                </DropdownMenu.Item>
                <DropdownMenu.ItemWithConfirm
                  icon={Icons.restoreDocument}
                  onConfirm={() => restoreJournal(journal.id)}
                >
                  Restore
                </DropdownMenu.ItemWithConfirm>
                <DropdownMenu.ItemWithConfirm onConfirm={deleteJournal}>
                  Delete
                </DropdownMenu.ItemWithConfirm>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          )}
        </XStack>
      }
    />
  );
});

export { JournalsHeader as JournalsHeader };

interface DateLabelProps {
  journalId: string;
}

const DateLabel = observer(({ journalId }: DateLabelProps) => {
  const { journals: store } = useStores();

  const journal = journalId
    ? computed(() => store.data.get(journalId)).get()
    : null;

  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => setRefreshCount((prev) => prev + 1), 2500);
    return () => clearTimeout(timeout);
  }, [journal?.updatedAt]);

  useEffect(() => {
    if (journal?.date) {
      trackEvent('JOURNAL_OPENED', {
        date: journal.date,
      });
    }
  }, [journal]);

  if (!journal) return null;

  return (
    <Button
      size="xs"
      variant="ghost"
      className="gap-1.5 px-0 mr-2 text-xs.5 text-foreground-muted hover:bg-transparent hover:text-foreground-subtle"
      key={`date-${refreshCount}`}
      tooltip={
        journal?.updatedAt
          ? `Edited ${formatDateHumanReadable(journal.updatedAt)} · Curated ${formatDateHumanReadable(journal?.createdAt)}`
          : `Curated ${formatDateHumanReadable(journal?.createdAt)}`
      }
      tooltipOptions={{ side: 'bottom' }}
    >
      {journal.isEdited && journal?.updatedAt ? 'Edited' : 'Curated'}{' '}
      {formatDateHumanReadable(journal?.updatedAt ?? journal?.createdAt)}
    </Button>
  );
});

const ShowHistoryButton = observer(() => {
  return (
    <IconButton
      data-active={journalStore.showHistory}
      size="sm"
      className="hidden md:block"
      icon={Icons.history}
      tooltip="History"
      onClick={() => journalStore.toggleShowHistory()}
    />
  );
});
